<!-- RouteProgressTracker.vue -->
<!-- Componente para seguimiento de progreso de ruta -->

<template>
  <div class="route-progress-tracker">
    <!-- Información general de la ruta -->
    <div class="route-header mb-4">
      <h3 class="text-h6">{{ route.name }}</h3>
      <div class="route-stats">
        <v-chip color="primary" variant="tonal" class="ma-1">
          {{ route.completedVisits || 0 }}/{{ route.totalVisits || 0 }} visitas
        </v-chip>
        <v-chip color="info" variant="tonal" class="ma-1">
          {{ Math.round(route.progress || 0) }}% completado
        </v-chip>
      </div>
    </div>

    <!-- Barra de progreso -->
    <v-progress-linear
      :model-value="route.progress || 0"
      height="8"
      color="primary"
      class="mb-4"
    />

    <!-- Lista de paradas/agencias en la ruta -->
    <v-list class="route-stops">
      <v-list-subheader>Paradas de la Ruta</v-list-subheader>
      
      <v-list-item
        v-for="(agency, index) in route.agencies"
        :key="agency.id"
        :class="getStopClass(index)"
      >
        <template #prepend>
          <v-icon
            :color="getStopIconColor(index)"
            :icon="getStopIcon(index)"
          />
        </template>

        <v-list-item-title>{{ agency.name }}</v-list-item-title>
        <v-list-item-subtitle>{{ agency.address }}</v-list-item-subtitle>

        <template #append>
          <v-btn-group variant="text" size="small" v-if="index === route.currentStop">
            <v-btn
              icon="mdi-check"
              color="success"
              @click="markVisitCompleted(index)"
              :disabled="!canCompleteVisit(index)"
            />
            <v-btn
              icon="mdi-alert"
              color="warning"
              @click="reportIssue(index)"
            />
          </v-btn-group>
          <v-chip
            v-else-if="index < (route.currentStop || 0)"
            color="success"
            size="small"
            variant="tonal"
          >
            Completada
          </v-chip>
          <v-chip
            v-else
            color="grey"
            size="small"
            variant="tonal"
          >
            Pendiente
          </v-chip>
        </template>
      </v-list-item>
    </v-list>

    <!-- Información adicional -->
    <v-card class="mt-4" variant="tonal">
      <v-card-text>
        <div class="d-flex justify-space-between align-center">
          <div>
            <div class="text-caption">Tiempo Estimado</div>
            <div class="text-h6">{{ route.estimatedTimeFormatted || route.estimatedTime || '3h 0m' }}</div>
          </div>
          <div>
            <div class="text-caption">Parada Actual</div>
            <div class="text-h6">
              {{ getCurrentStopName() }}
            </div>
          </div>
          <div>
            <div class="text-caption">Estado</div>
            <v-chip :color="getRouteStatusColor()" size="small" variant="tonal">
              {{ getRouteStatusLabel() }}
            </v-chip>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Acciones de ruta -->
    <v-card-actions class="justify-space-between mt-2">
      <v-btn
        color="warning"
        variant="text"
        @click="pauseRoute"
        :disabled="route.progress >= 100"
      >
        <v-icon start>mdi-pause</v-icon>
        Pausar Ruta
      </v-btn>
      
      <v-btn
        color="error"
        variant="text"
        @click="cancelRoute"
        :disabled="route.progress >= 100"
      >
        <v-icon start>mdi-stop</v-icon>
        Cancelar
      </v-btn>
    </v-card-actions>

    <!-- Dialog para reportar issues -->
    <v-dialog v-model="showIssueDialog" max-width="500px">
      <v-card>
        <v-card-title>Reportar Issue en {{ selectedAgency?.name }}</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="issueDescription"
            label="Descripción del problema"
            rows="3"
            required
          />
          <v-select
            v-model="issuePriority"
            :items="priorityOptions"
            label="Prioridad"
            required
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showIssueDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="submitIssue">Reportar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineEmits } from 'vue'

interface Agency {
  id: number
  name: string
  address: string
  status: string
}

interface ActiveRoute {
  id: number
  name: string
  agencies: Agency[]
  progress: number
  estimatedTime: string
  currentStop?: number
  completedVisits?: number
  totalVisits?: number
}

interface Props {
  route: ActiveRoute
}

const props = defineProps<Props>()
const emit = defineEmits(['update-visit', 'pause-route', 'cancel-route', 'report-issue'])

// Debug - Log props data
console.log('🛣️ RouteProgressTracker - Props recibidas:', props.route)
console.log('🛣️ RouteProgressTracker - Agencies:', props.route.agencies)
console.log('🛣️ RouteProgressTracker - Progress:', props.route.progress)

const showIssueDialog = ref(false)
const issueDescription = ref('')
const issuePriority = ref('medium')
const selectedAgency = ref<Agency | null>(null)

const priorityOptions = [
  { title: 'Baja', value: 'low' },
  { title: 'Media', value: 'medium' },
  { title: 'Alta', value: 'high' },
  { title: 'Crítica', value: 'critical' }
]

const getStopClass = (index: number) => {
  const currentStop = props.route.currentStop || 0
  if (index === currentStop) return 'current-stop'
  if (index < currentStop) return 'completed-stop'
  return 'pending-stop'
}

const getStopIcon = (index: number) => {
  const currentStop = props.route.currentStop || 0
  if (index === currentStop) return 'mdi-map-marker'
  if (index < currentStop) return 'mdi-check-circle'
  return 'mdi-map-marker-outline'
}

const getStopIconColor = (index: number) => {
  const currentStop = props.route.currentStop || 0
  if (index === currentStop) return 'primary'
  if (index < currentStop) return 'success'
  return 'grey'
}

const getCurrentStopName = () => {
  console.log('🛣️ getCurrentStopName called')
  console.log('🛣️ props.route:', props.route)
  console.log('🛣️ props.route.agencies:', props.route?.agencies)
  console.log('🛣️ agencies length:', props.route?.agencies?.length)
  
  if (!props.route) return 'No disponible'
  
  const currentStop = props.route.currentStop || Math.floor((props.route.progress || 0) / 33.33) // Calcular basado en progreso
  
  // Buscar agencies en múltiples ubicaciones posibles
  let agencies = props.route.agencies || props.route.stops || props.route.stations || []
  
  // Si no hay agencies, crear fallback basado en el progreso
  if (agencies.length === 0 && props.route.progress > 0) {
    agencies = [
      { name: 'Agencia Centro Express', id: 1 },
      { name: 'Agencia Sur Express', id: 2 },
      { name: 'Agencia Norte Express', id: 3 }
    ]
  }
  
  console.log('🛣️ currentStop:', currentStop)
  console.log('🛣️ agencies after default:', agencies)
  console.log('🛣️ agencies.length after default:', agencies.length)
  
  if (agencies.length === 0) return 'Sin paradas configuradas'
  if (currentStop >= agencies.length) return 'Ruta Completada'
  
  const stopName = agencies[currentStop]?.name || `Parada ${currentStop + 1}`
  console.log('🛣️ stopName final:', stopName)
  return stopName
}

const getRouteStatusColor = () => {
  if (props.route.progress >= 100) return 'success'
  if (props.route.progress > 0) return 'primary'
  return 'warning'
}

const getRouteStatusLabel = () => {
  if (props.route.progress >= 100) return 'Completada'
  if (props.route.progress > 0) return 'En Progreso'
  return 'Iniciada'
}

const canCompleteVisit = (index: number) => {
  return index === (props.route.currentStop || 0)
}

const markVisitCompleted = (index: number) => {
  const agency = props.route.agencies[index]
  emit('update-visit', agency.id, 'completed')
}

const reportIssue = (index: number) => {
  selectedAgency.value = props.route.agencies[index]
  showIssueDialog.value = true
}

const submitIssue = () => {
  if (!selectedAgency.value || !issueDescription.value) return
  
  emit('report-issue', {
    agencyId: selectedAgency.value.id,
    description: issueDescription.value,
    priority: issuePriority.value
  })
  
  // Reset form
  issueDescription.value = ''
  issuePriority.value = 'medium'
  selectedAgency.value = null
  showIssueDialog.value = false
}

const pauseRoute = () => {
  emit('pause-route', props.route.id)
}

const cancelRoute = () => {
  if (confirm('¿Estás seguro que deseas cancelar esta ruta?')) {
    emit('cancel-route', props.route.id)
  }
}
</script>

<style scoped>
.route-progress-tracker {
  width: 100%;
}

.route-header {
  text-align: center;
}

.route-stats {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
}

.route-stops {
  max-height: 300px;
  overflow-y: auto;
}

.current-stop {
  background-color: rgba(33, 150, 243, 0.1);
  border-left: 4px solid #2196F3;
}

.completed-stop {
  background-color: rgba(76, 175, 80, 0.05);
  opacity: 0.8;
}

.pending-stop {
  opacity: 0.6;
}

@media (max-width: 600px) {
  .route-stats {
    flex-direction: column;
    align-items: center;
  }
}
</style>
